import React, { useState, useEffect } from 'react';
import {
  ArrowDownCircle,
  Users,
  User,
  Wallet,
  DollarSign,
  Activity,
  UserPlus,
  RefreshCw,
} from 'lucide-react';
import api from '../utils/axios';

/* ── Design tokens ── */
const GBORDER = '#E8EBF4';
const CARD = '#FFFFFF';
const MUTED = '#6D7588';

/* ── Helpers ── */
const formatNumber = (v?: number) => Number(v ?? 0).toFixed(2);

/* ─────────────────────────────────────────────
   InfoBox Component (AdminLTE Style)
───────────────────────────────────────────── */
const InfoBox = ({
  label,
  value,
  color,
  icon: Icon,
}: {
  label: string;
  value: string | number;
  color: string;
  icon: React.ComponentType<any>;
}) => {
  return (
    <div
      style={{
        display: 'flex',
        background: '#fff',
        borderRadius: '4px',
        boxShadow: '0 0 1px rgba(0,0,0,.125), 0 1px 3px rgba(0,0,0,.15)',
        minHeight: '80px',
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          width: '75px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: color,
          color: '#fff',
          flexShrink: 0,
        }}
      >
        <Icon size={28} color="#fff" strokeWidth={2} />
      </div>
      <div
        style={{
          padding: '10px 15px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          flexGrow: 1,
          minWidth: 0,
        }}
      >
        <span
          style={{
            fontSize: '14px',
            color: '#333333',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
        </span>
        <span
          style={{
            fontSize: '18px',
            fontWeight: 700,
            color: '#111111',
            marginTop: '2px',
          }}
        >
          {value}
        </span>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   Main Dashboard / Home
═══════════════════════════════════════════ */
const AdminDashboard = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/getDashboardData');
      setData(res.data?.data ?? {});
      setLastUpdated(new Date());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', padding: '20px 0' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 16, marginBottom: 16,
        }}>
          {[...Array(8)].map((_, i) => (
            <div key={i} style={{
              height: 80, background: CARD, borderRadius: 4,
              border: `1px solid ${GBORDER}`,
              animation: 'pulse 1.5s infinite',
            }} />
          ))}
        </div>
        <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.5}}`}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', fontFamily: "'Inter', sans-serif" }} className="dash-root">

      {/* ── Page Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 500, color: '#333333', margin: 0 }}>Home</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* <span style={{
            fontSize: 12, color: MUTED, background: CARD,
            border: `1px solid ${GBORDER}`, borderRadius: 4,
            padding: '6px 12px',
          }}>
            Updated: {lastUpdated.toLocaleTimeString()}
          </span> */}
          <button
            onClick={fetchData}
            style={{
              display: 'flex', alignItems: 'center', gap: 7,
              padding: '7px 16px', borderRadius: 4,
              background: '#333333', color: '#fff',
              fontSize: 13, fontWeight: 600,
              border: 'none', cursor: 'pointer',
            }}
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* ── 8 Stat Cards Grid ── */}
      <div className="dash-grid-4" style={{ marginBottom: 16 }}>
        <InfoBox
          label="Today Deposits"
          value={formatNumber(data?.todayDeposits)}
          color="#17a2b8"
          icon={Wallet}
        />
        <InfoBox
          label="Today Withdrawals"
          value={formatNumber(data?.todayWithdrawals)}
          color="#dc3545"
          icon={ArrowDownCircle}
        />
        <InfoBox
          label="New Players"
          value={String(data?.newPlayers ?? 0)}
          color="#ffc107"
          icon={Users}
        />
        <InfoBox
          label="Active Players"
          value={String(data?.activePlayers ?? data?.totalUsers ?? 0)}
          color="#6c757d"
          icon={User}
        />
        <InfoBox
          label="Today Win Loss"
          value={formatNumber(data?.todayWinLoss)}
          color="#28a745"
          icon={DollarSign}
        />
        <InfoBox
          label="Monthly Win Loss"
          value={formatNumber(data?.monthlyWinLoss)}
          color="#007bff"
          icon={Activity}
        />
        <InfoBox
          label="Monthly Cash Back Bounce"
          value={formatNumber(data?.monthlyCashBackBounce)}
          color="#28a745"
          icon={DollarSign}
        />
        <InfoBox
          label="Monthly Refer Bounce"
          value={formatNumber(data?.monthlyReferBounce)}
          color="#17a2b8"
          icon={UserPlus}
        />
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

        /* 4-column grid → 2 on tablet → 1 on mobile */
        .dash-grid-4 {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
        }

        /* Tablet: ≤900px */
        @media (max-width: 900px) {
          .dash-grid-4 { grid-template-columns: repeat(2, 1fr); }
        }

        /* Mobile: ≤600px */
        @media (max-width: 600px) {
          .dash-grid-4 { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default AdminDashboard;